/* Release notes for the Yenilikler page and the version shown in the footer and Ayarlar.
   CHANGELOG.md is written by `pnpm version-packages` from the bilingual changesets in .changeset/
   (format: .changeset/README.md). Each release looks like
     ## 2.1.0 — 2026-11-02
     ### Minor Changes
     - tr: Şarkılar'a "Bu Akşam" (Duman) eklendi.\
       en: Added "Bu Akşam" (Duman) to Şarkılar.
   and the site shows the Turkish line of every note. */
import changelog from '../../../CHANGELOG.md?raw';
import { version } from '../../../package.json';

export interface ReleaseNote {
  tr: string;
  en: string;
}

export interface Release {
  version: string;
  /** Release day as YYYY-MM-DD, or null when the heading has none. */
  date: string | null;
  notes: ReleaseNote[];
}

/** Version of this build (package.json; `pnpm version-packages` raises it). */
export const APP_VERSION: string = version;

const HEADING = /^##\s+(\d+\.\d+\.\d+\S*)(?:\s+[—–-]\s+(\d{4}-\d{2}-\d{2}))?\s*$/;
// "- tr: …" starts a note; an indented "en: …" (or "tr: …") adds the other language to it.
const NOTE_LINE = /^(\s*-\s+|\s+)(tr|en):\s*(.*)$/i;

export function parseChangelog(md: string): Release[] {
  const releases: { version: string; date: string | null; notes: Partial<ReleaseNote>[] }[] = [];
  let lang: keyof ReleaseNote | null = null;
  for (const raw of md.split(/\r?\n/)) {
    const head = HEADING.exec(raw);
    if (head) {
      releases.push({ version: head[1] as string, date: head[2] ?? null, notes: [] });
      lang = null;
      continue;
    }
    const release = releases.at(-1);
    // a trailing backslash is a Markdown line break between the two languages
    const line = raw.replace(/\\$/, '').trimEnd();
    if (!release || !line.trim()) continue;
    const last = release.notes.at(-1);
    const m = NOTE_LINE.exec(line);
    if (m) {
      lang = (m[2] as string).toLowerCase() as keyof ReleaseNote;
      const text = (m[3] as string).trim();
      if (m[1]?.includes('-') || !last || last[lang] != null) release.notes.push({ [lang]: text });
      else last[lang] = text;
    } else if (lang && last && /^\s/.test(line)) {
      last[lang] = `${last[lang] ?? ''} ${line.trim()}`.trim();
    } else {
      // "### Minor Changes" and any other text end the current note
      lang = null;
    }
  }
  return releases.map((r) => ({
    ...r,
    notes: r.notes.flatMap((n) => (n.tr ? [{ tr: n.tr, en: n.en ?? '' }] : [])),
  }));
}

/** Every release, newest first. */
export const RELEASES: readonly Release[] = parseChangelog(changelog);

/** Notes of the running version (null while it has no CHANGELOG.md section). */
export const CURRENT_RELEASE: Release | null = RELEASES.find((r) => r.version === APP_VERSION) ?? null;
